import { useMemo, useState } from 'react';
import {
  BUILD_CHROM_LENGTHS,
  CYTOBANDS,
  IDEOGRAM_CHROMS,
  STAINS,
  type GenomeBuild,
} from '../lib/cytobands';
import { CONDITIONS } from '../lib/conditions';
import type { ParsedGenome } from '../types';
import { Explain } from './Explain';

interface Props {
  genome: ParsedGenome;
}

/**
 * Prefer what the file actually says. A VCF header carries `##reference=` and
 * `##contig=<...,length=N>`, and a contig length is a fact rather than a label —
 * GRCh37 chromosome 1 is 249,250,621 and GRCh38 is 248,956,422.
 *
 * Only when the header says nothing do we fall back to the file format: array
 * exports are GRCh37, VCFs are usually GRCh38. That fallback is a guess and the
 * UI says so.
 */
function defaultBuild(genome: ParsedGenome): GenomeBuild {
  if (genome.meta.assembly) return genome.meta.assembly;
  return genome.format === 'vcf' ? 'hg38' : 'hg19';
}

const LANE_W = 13; // drawn chromosome width
const GAP = 24; // horizontal gap between chromosomes
const TOP = 18; // room for the chromosome label
const MAX_H = 340; // px for the longest chromosome
const DOT_R = 2.6;

/** How many variants fall in each vertical pixel band — cheap density track. */
const DENSITY_BINS = 60;

export function Ideogram({ genome }: Props) {
  const [build, setBuild] = useState<GenomeBuild>(() => defaultBuild(genome));
  const [hover, setHover] = useState<string | null>(null);
  const [showMarkers, setShowMarkers] = useState(true);
  const detected = genome.meta.assembly ?? null;

  const lengths = BUILD_CHROM_LENGTHS[build];
  const bands = CYTOBANDS[build];
  const maxLen = Math.max(...IDEOGRAM_CHROMS.map((c) => lengths[c] ?? 0));

  /** Variants bucketed per chromosome, plus the catalog markers we can place. */
  const { byChrom, markerDots, placed, unplaced, overrun } = useMemo(() => {
    const catalogRs = new Set<string>();
    for (const c of CONDITIONS) for (const m of c.markers) catalogRs.add(m.rsid.toLowerCase());

    const byChrom = new Map<string, number[]>();
    const markerDots = new Map<string, { pos: number; rsid: string }[]>();
    let placed = 0;
    let unplaced = 0;
    // Positions past the end of their chromosome under this assembly. Almost
    // always means the assembly is wrong, so it is counted and surfaced rather
    // than clamped — a clamped dot looks like data, an omitted one looks like
    // a bug, and the honest answer is "these do not fit".
    let overrun = 0;

    for (const v of genome.variants) {
      const len = lengths[v.chrom];
      if (!len) {
        unplaced += 1; // MT and any unrecognised contig — no bands to draw on
        continue;
      }
      if (v.pos > len) {
        overrun += 1;
        continue;
      }
      placed += 1;
      let arr = byChrom.get(v.chrom);
      if (!arr) byChrom.set(v.chrom, (arr = []));
      arr.push(v.pos);

      if (catalogRs.has(v.rsid.toLowerCase())) {
        let dots = markerDots.get(v.chrom);
        if (!dots) markerDots.set(v.chrom, (dots = []));
        dots.push({ pos: v.pos, rsid: v.rsid });
      }
    }
    return { byChrom, markerDots, placed, unplaced, overrun };
  }, [genome, lengths]);

  const width = IDEOGRAM_CHROMS.length * (LANE_W + GAP);
  const height = TOP + MAX_H + 14;

  return (
    <div className="card">
      <h2>
        Genome map <span className="muted">· {IDEOGRAM_CHROMS.length} chromosomes · {build}</span>
        <Explain id="ideogram" />
      </h2>

      <div className="controls">
        <label className="inline-control">
          Assembly
          <select
            value={build}
            onChange={(e) => setBuild(e.target.value as GenomeBuild)}
            aria-label="Genome assembly"
          >
            <option value="hg19">
              GRCh37 / hg19 — 23andMe, AncestryDNA{detected === 'hg19' ? ' · from file header' : ''}
            </option>
            <option value="hg38">
              GRCh38 / hg38 — most VCFs{detected === 'hg38' ? ' · from file header' : ''}
            </option>
          </select>
        </label>
        <label className="inline-control">
          <input
            type="checkbox"
            checked={showMarkers}
            onChange={(e) => setShowMarkers(e.target.checked)}
          />
          Show condition markers
        </label>
      </div>

      <div className="ideogram-scroll">
        <svg
          className="ideogram"
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label={`Ideogram of ${IDEOGRAM_CHROMS.length} chromosomes with your variant positions`}
        >
          {IDEOGRAM_CHROMS.map((chrom, i) => {
            const len = lengths[chrom];
            if (!len) return null;
            const x = i * (LANE_W + GAP) + GAP / 2;
            const h = (len / maxLen) * MAX_H;
            const scale = (bp: number) => TOP + (bp / len) * h;
            const chromBands = bands[chrom] ?? [];
            const positions = byChrom.get(chrom) ?? [];
            const dots = showMarkers ? (markerDots.get(chrom) ?? []) : [];

            // Density track: count into fixed bins so a 600k-variant file still
            // renders as O(bins) rects rather than O(variants).
            const binH = h / DENSITY_BINS;
            const bins = new Array(DENSITY_BINS).fill(0);
            for (const p of positions) {
              const b = Math.min(DENSITY_BINS - 1, Math.floor((p / len) * DENSITY_BINS));
              bins[b] += 1;
            }
            const peak = Math.max(1, ...bins);

            return (
              <g key={chrom}>
                <text className="ideo-label" x={x + LANE_W / 2} y={12} textAnchor="middle">
                  {chrom}
                </text>

                {/* Density track, just left of the chromosome */}
                {bins.map((n, b) =>
                  n === 0 ? null : (
                    <rect
                      key={b}
                      className="ideo-density"
                      x={x - 5}
                      y={TOP + b * binH}
                      width={4}
                      height={Math.max(0.6, binH)}
                      opacity={0.25 + 0.75 * (n / peak)}
                    />
                  ),
                )}

                {/* Bands, clipped to a rounded chromosome outline */}
                <clipPath id={`clip-${build}-${chrom}`}>
                  <rect x={x} y={TOP} width={LANE_W} height={h} rx={LANE_W / 2} />
                </clipPath>
                <g clipPath={`url(#clip-${build}-${chrom})`}>
                  {chromBands.map(([start, end, name, stainIdx]) => {
                    const y = scale(start);
                    const bh = Math.max(0.5, scale(end) - y);
                    const stain = STAINS[stainIdx];
                    const key = `${chrom}:${name}`;
                    return (
                      <rect
                        key={key}
                        className={`ideo-band stain-${stain}`}
                        x={x}
                        y={y}
                        width={LANE_W}
                        height={bh}
                        onMouseEnter={() => setHover(`${chrom}${name} · ${stain}`)}
                        onMouseLeave={() => setHover(null)}
                      >
                        <title>{`${chrom}${name} — ${stain}`}</title>
                      </rect>
                    );
                  })}
                </g>
                <rect
                  className="ideo-outline"
                  x={x}
                  y={TOP}
                  width={LANE_W}
                  height={h}
                  rx={LANE_W / 2}
                />

                {/* Catalog markers present in this file */}
                {dots.map((d, k) => (
                  <circle
                    key={`${d.rsid}-${k}`}
                    className="ideo-marker"
                    cx={x + LANE_W + 4}
                    cy={scale(d.pos)}
                    r={DOT_R}
                    onMouseEnter={() => setHover(`${d.rsid} · chr${chrom}:${d.pos.toLocaleString()}`)}
                    onMouseLeave={() => setHover(null)}
                  >
                    <title>{`${d.rsid} — chr${chrom}:${d.pos.toLocaleString()}`}</title>
                  </circle>
                ))}
              </g>
            );
          })}
        </svg>
      </div>

      <div className="ideo-footer">
        <div className="ideo-legend">
          <span className="ideo-key"><i className="swatch stain-gneg" />light band</span>
          <span className="ideo-key"><i className="swatch stain-gpos100" />dark band</span>
          <span className="ideo-key"><i className="swatch stain-acen" />centromere</span>
          <span className="ideo-key"><i className="swatch density" />your variant density</span>
          <span className="ideo-key"><i className="swatch marker" />condition marker</span>
        </div>
        <div className="ideo-hover mono">{hover ?? ' '}</div>
      </div>

      <div className="note info" style={{ marginTop: 14 }}>
        {placed.toLocaleString()} of {genome.variants.length.toLocaleString()} variants
        plotted{unplaced > 0 && ` · ${unplaced.toLocaleString()} on MT or unplaced contigs, which have no cytogenetic bands`}.
        Positions are interpreted as <strong>{build === 'hg19' ? 'GRCh37/hg19' : 'GRCh38/hg38'}</strong>
        {detected && build === detected
          ? genome.meta.assemblySource === 'contig-length'
            ? ' — read from the contig lengths in the VCF header, so this is certain rather than assumed.'
            : ' — read from the ##reference line in the VCF header.'
          : detected
            ? ` — but the file's own header says ${detected === 'hg19' ? 'GRCh37' : 'GRCh38'}. You have overridden it.`
            : genome.format === 'vcf'
              ? ' — assumed, because the header names no assembly. If yours is GRCh37, switch above.'
              : ' — assumed, because 23andMe and AncestryDNA export GRCh37 coordinates and array files carry no assembly field.'}
      </div>

      {overrun > 0 && (
        <div className="note" style={{ marginTop: 10 }}>
          <strong>{overrun.toLocaleString()} position{overrun === 1 ? '' : 's'} fall past the end
          of their chromosome under {build === 'hg19' ? 'GRCh37' : 'GRCh38'}</strong>, so they are
          not drawn. That usually means the assembly above is the wrong one for this file — try
          switching it. If the count stays high in both, the coordinates may come from an assembly
          this app does not carry.
        </div>
      )}
    </div>
  );
}
